import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Coins,
  Calculator,
  FileText,
  CheckCircle,
  Clock,
  Building2,
  TrendingUp,
  Plus,
  Trash2,
  IndianRupee,
  Download,
  CalendarDays,
  FileSpreadsheet,
} from "lucide-react";
import { formatCurrency } from "../lib/currency";
import { usePayroll } from "../hooks";
// Extracted components
import {
  PayrollStats,
  PayrollTable,
  BulkGenerateDialog,
  ExportDialog,
  PayslipDialog,
} from "../components/payroll";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const Payroll = () => {
  const { currentBusiness, user } = useAuth();
  const fmt = (amount) => formatCurrency(amount, currentBusiness?.currency);
  
  // Use custom hook for main data fetching
  const {
    payrolls,
    employees,
    loading,
    stats,
    fetchData,
    createPayroll,
    generateBulkPayroll,
    processPayroll,
    autoCalculate,
    manualCalculate,
    fetchPendingAdvances,
    fetchAttendanceSummary,
    downloadPayslipPDF,
    exportPayroll,
    getEmployeeName,
  } = usePayroll(currentBusiness?.id);

  // Local UI state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(months[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [calculating, setCalculating] = useState(false);
  
  // Bulk generation options
  const [bulkEnablePF, setBulkEnablePF] = useState(true);
  const [bulkEnableESI, setBulkEnableESI] = useState(true);
  const [bulkEnablePT, setBulkEnablePT] = useState(false);
  
  // Export state - for ExportDialog component
  const [exportStartMonth, setExportStartMonth] = useState("");
  const [exportEndMonth, setExportEndMonth] = useState("");
  const [exportEmployee, setExportEmployee] = useState("all");
  const [exportStatus, setExportStatus] = useState("all");
  const [exportFormat, setExportFormat] = useState("excel");
  const [exporting, setExporting] = useState(false);

  const [formData, setFormData] = useState({
    employee_id: "",
    month: months[new Date().getMonth()],
    year: new Date().getFullYear().toString(),
    basic_salary: "",
    hra: "",
    conveyance_allowance: "",
    special_allowance: "",
    other_allowances: "0",
    custom_deductions: [],
    enable_pf: true,
    enable_esi: true,
    enable_pt: false,
  });

  const [computed, setComputed] = useState(null);
  const [pendingAdvances, setPendingAdvances] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // Fetch pending advances when employee is selected
  useEffect(() => {
    const loadAdvances = async () => {
      if (formData.employee_id && currentBusiness?.id) {
        try {
          const advances = await fetchPendingAdvances(formData.employee_id);
          setPendingAdvances(advances);
        } catch {
          setPendingAdvances([]);
        }
      } else {
        setPendingAdvances([]);
      }
    };
    loadAdvances();
  }, [formData.employee_id, currentBusiness?.id, fetchPendingAdvances]);

  // Fetch attendance summary (LOP) when employee and month/year are selected
  useEffect(() => {
    const loadAttendanceSummary = async () => {
      if (formData.employee_id && formData.month && formData.year && currentBusiness?.id) {
        setLoadingAttendance(true);
        try {
          const emp = employees.find(e => e.id === formData.employee_id);
          const grossSalary = emp?.salary || 0;
          const summary = await fetchAttendanceSummary(formData.employee_id, formData.month, formData.year, grossSalary);
          setAttendanceSummary(summary);
        } catch {
          setAttendanceSummary(null);
        } finally {
          setLoadingAttendance(false);
        }
      } else {
        setAttendanceSummary(null);
      }
    };
    loadAttendanceSummary();
  }, [formData.employee_id, formData.month, formData.year, currentBusiness?.id, employees, fetchAttendanceSummary]);

  const handleAutoCalculate = async () => {
    const salary = parseFloat(formData.basic_salary);
    if (!salary || salary <= 0) {
      toast.error("Enter a valid CTC / gross salary");
      return;
    }
    if (!formData.employee_id) {
      toast.error("Please select an employee first");
      return;
    }
    setCalculating(true);
    try {
      const customDedStr = JSON.stringify(formData.custom_deductions.filter(d => d.name && d.amount > 0));
      const lopDays = attendanceSummary?.lop_days || 0;
      const lopAmount = attendanceSummary?.lop_amount || 0;
      const workingDays = attendanceSummary?.working_days || 0;
      const presentDays = attendanceSummary?.present_days || 0;
      
      const data = await autoCalculate({
        ctc_monthly: salary,
        custom_deductions: customDedStr,
        enable_pf: formData.enable_pf,
        enable_esi: formData.enable_esi,
        enable_pt: formData.enable_pt,
        employee_id: formData.employee_id,
        business_id: currentBusiness.id,
        lop_days: lopDays,
        lop_amount: lopAmount,
        working_days: workingDays,
        present_days: presentDays,
      });
      setFormData(prev => ({
        ...prev,
        basic_salary: data.basic_salary.toString(),
        hra: data.hra.toString(),
        conveyance_allowance: data.conveyance_allowance.toString(),
        special_allowance: data.special_allowance.toString(),
      }));
      setComputed(data);
    } catch {
      toast.error("Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const handleManualCalculate = async () => {
    const basic = parseFloat(formData.basic_salary) || 0;
    if (basic <= 0) {
      toast.error("Enter a valid basic salary");
      return;
    }
    if (!formData.employee_id) {
      toast.error("Please select an employee first");
      return;
    }
    setCalculating(true);
    try {
      const customDedStr = JSON.stringify(formData.custom_deductions.filter(d => d.name && d.amount > 0));
      const lopDays = attendanceSummary?.lop_days || 0;
      const lopAmount = attendanceSummary?.lop_amount || 0;
      const workingDays = attendanceSummary?.working_days || 0;
      const presentDays = attendanceSummary?.present_days || 0;
      
      const data = await manualCalculate({
        basic_salary: basic,
        hra: parseFloat(formData.hra) || 0,
        conveyance_allowance: parseFloat(formData.conveyance_allowance) || 0,
        special_allowance: parseFloat(formData.special_allowance) || 0,
        other_allowances: parseFloat(formData.other_allowances) || 0,
        custom_deductions: customDedStr,
        enable_pf: formData.enable_pf,
        enable_esi: formData.enable_esi,
        enable_pt: formData.enable_pt,
        employee_id: formData.employee_id,
        business_id: currentBusiness.id,
        lop_days: lopDays,
        lop_amount: lopAmount,
        working_days: workingDays,
        present_days: presentDays,
      });
      setComputed(data);
    } catch {
      toast.error("Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id) {
      toast.error("Please select an employee");
      return;
    }
    if (!computed) {
      toast.error("Please calculate salary first");
      return;
    }
    try {
      await createPayroll({
        employee_id: formData.employee_id,
        month: formData.month,
        year: parseInt(formData.year),
        basic_salary: computed.basic_salary,
        hra: computed.hra,
        conveyance_allowance: computed.conveyance_allowance,
        special_allowance: computed.special_allowance,
        other_allowances: computed.other_allowances,
        custom_deductions: formData.custom_deductions.filter(d => d.name && d.amount > 0),
        enable_pf: formData.enable_pf,
        enable_esi: formData.enable_esi,
        enable_pt: formData.enable_pt,
      });
      toast.success("Payroll record created");
      setDialogOpen(false);
      resetForm();
    } catch {
      toast.error("Failed to create payroll record");
    }
  };

  const handleGenerateBulk = async () => {
    try {
      const response = await generateBulkPayroll(selectedMonth, parseInt(selectedYear), {
        enable_pf: bulkEnablePF,
        enable_esi: bulkEnableESI,
        enable_pt: bulkEnablePT,
      });
      toast.success(response.message);
      setGenerateDialogOpen(false);
    } catch {
      toast.error("Failed to generate payroll");
    }
  };

  const handleProcess = async (id) => {
    try {
      await processPayroll(id);
      toast.success("Payroll processed successfully");
    } catch {
      toast.error("Failed to process payroll");
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      month: months[new Date().getMonth()],
      year: new Date().getFullYear().toString(),
      basic_salary: "",
      hra: "",
      conveyance_allowance: "",
      special_allowance: "",
      other_allowances: "0",
      custom_deductions: [],
    });
    setComputed(null);
    setAttendanceSummary(null);
    setPendingAdvances([]);
  };

  const addCustomDeduction = () => {
    setFormData(prev => ({
      ...prev,
      custom_deductions: [...prev.custom_deductions, { name: "", amount: 0 }]
    }));
  };

  const updateCustomDeduction = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.custom_deductions];
      updated[index] = { ...updated[index], [field]: field === "amount" ? parseFloat(value) || 0 : value };
      return { ...prev, custom_deductions: updated };
    });
  };

  const removeCustomDeduction = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_deductions: prev.custom_deductions.filter((_, i) => i !== index)
    }));
  };

  const viewPayrollDetail = (payroll) => {
    setSelectedPayroll(payroll);
    setDetailDialogOpen(true);
  };

  const handleDownloadPayslip = async (payrollId) => {
    try {
      const blob = await downloadPayslipPDF(payrollId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip_${payrollId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Payslip downloaded");
    } catch {
      toast.error("Failed to download payslip");
    }
  };

  const handleExport = async () => {
    if (!exportStartMonth || !exportEndMonth) {
      toast.error("Please select both start and end months");
      return;
    }
    
    setExporting(true);
    try {
      const blob = await exportPayroll(exportStartMonth, exportEndMonth, exportFormat, exportEmployee, exportStatus);
      const downloadUrl = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const empName = exportEmployee !== "all" 
        ? employees.find(e => e.id === exportEmployee)
        : null;
      const empPart = empName ? `_${empName.first_name}_${empName.last_name}` : "";
      const ext = exportFormat === "excel" ? "xlsx" : exportFormat;
      link.setAttribute('download', `payroll${empPart}_${exportStartMonth}_to_${exportEndMonth}.${ext}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completed");
      setExportDialogOpen(false);
    } catch {
      toast.error("Failed to export payroll");
    } finally {
      setExporting(false);
    }
  };

  const canManagePayroll = ["super_admin", "business_admin", "hr_manager"].includes(user?.role);

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage payroll</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Payroll Management</h1>
          <p className="text-slate-500">Indian payroll with PF, ESI, PT (optional) & TDS</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {/* Export Dialog */}
          <Dialog open={exportDialogOpen} onOpenChange={(open) => { 
            setExportDialogOpen(open); 
            if (!open) {
              setExportStartMonth("");
              setExportEndMonth("");
              setExportEmployee("all");
              setExportStatus("all");
              setExportFormat("excel");
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="export-payroll-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Download className="h-5 w-5 text-primary" />
                  Export Payroll Report
                </DialogTitle>
                <DialogDescription>
                  Generate payroll report in your preferred format
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Format Selection */}
                <div className="space-y-2">
                  <Label>Export Format</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={exportFormat === "excel" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExportFormat("excel")}
                      className={exportFormat === "excel" ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                    <Button
                      variant={exportFormat === "csv" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExportFormat("csv")}
                      className={exportFormat === "csv" ? "bg-blue-600 hover:bg-blue-700" : ""}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button
                      variant={exportFormat === "pdf" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExportFormat("pdf")}
                      className={exportFormat === "pdf" ? "bg-red-600 hover:bg-red-700" : ""}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>

                {/* Employee Selection */}
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Select value={exportEmployee} onValueChange={setExportEmployee}>
                    <SelectTrigger className="input-field">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Selection */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={exportStatus} onValueChange={setExportStatus}>
                    <SelectTrigger className="input-field">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="processed">Processed</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Month Range Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Month</Label>
                    <Input
                      type="month"
                      value={exportStartMonth}
                      onChange={(e) => setExportStartMonth(e.target.value)}
                      className="input-field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Month</Label>
                    <Input
                      type="month"
                      value={exportEndMonth}
                      onChange={(e) => setExportEndMonth(e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>

                {/* Quick Month Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const now = new Date();
                    const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    setExportStartMonth(monthStr);
                    setExportEndMonth(monthStr);
                  }}>This Month</Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const now = new Date();
                    now.setMonth(now.getMonth() - 1);
                    const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    setExportStartMonth(monthStr);
                    setExportEndMonth(monthStr);
                  }}>Last Month</Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const now = new Date();
                    const startMonth = `${now.getFullYear()}-01`;
                    const endMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    setExportStartMonth(startMonth);
                    setExportEndMonth(endMonth);
                  }}>This Year</Button>
                </div>

                {/* Export Button */}
                <Button
                  className="w-full btn-primary"
                  onClick={handleExport}
                  disabled={!exportStartMonth || !exportEndMonth || exporting}
                >
                  {exporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download {exportFormat.toUpperCase()} Report
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {canManagePayroll && (
            <>
              <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="generate-payroll-btn">
                    <Calculator className="h-4 w-4 mr-2" />
                    Generate Bulk
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">Generate Bulk Payroll</DialogTitle>
                    <DialogDescription>Auto-calculate Indian payroll for all active employees based on their salary</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Month</Label>
                        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                          <SelectTrigger className="input-field" data-testid="bulk-month-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {months.map((month) => (
                              <SelectItem key={month} value={month}>{month}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Year</Label>
                        <Input
                          type="number"
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                          className="input-field"
                          data-testid="bulk-year-input"
                        />
                      </div>
                    </div>
                    
                    {/* Statutory Deductions Options */}
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
                      <Label className="text-sm font-medium text-slate-700">Statutory Deductions</Label>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="bulk-pf" 
                            checked={bulkEnablePF} 
                            onCheckedChange={setBulkEnablePF}
                            data-testid="bulk-enable-pf"
                          />
                          <Label htmlFor="bulk-pf" className="text-sm cursor-pointer">
                            PF (12%)
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="bulk-esi" 
                            checked={bulkEnableESI} 
                            onCheckedChange={setBulkEnableESI}
                            data-testid="bulk-enable-esi"
                          />
                          <Label htmlFor="bulk-esi" className="text-sm cursor-pointer">
                            ESI (0.75%)
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="bulk-pt" 
                            checked={bulkEnablePT} 
                            onCheckedChange={setBulkEnablePT}
                            data-testid="bulk-enable-pt"
                          />
                          <Label htmlFor="bulk-pt" className="text-sm cursor-pointer">
                            Prof. Tax
                          </Label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                      Salary will be auto-split: Basic (40%), HRA (20%), Conveyance, Special Allowance. Selected deductions & TDS calculated per Indian norms.
                    </div>
                    <Button onClick={handleGenerateBulk} className="w-full btn-primary" data-testid="confirm-generate-btn">
                      Generate Payroll
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-payroll-btn">
                    <Coins className="h-4 w-4 mr-2" />
                    Add Payroll
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Payroll</DialogTitle>
                    <DialogDescription>Indian salary structure with statutory deductions</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2 col-span-2">
                        <Label>Employee</Label>
                        <Select
                          value={formData.employee_id}
                          onValueChange={(value) => {
                            const emp = employees.find(e => e.id === value);
                            setFormData(prev => ({
                              ...prev,
                              employee_id: value,
                              basic_salary: emp?.salary?.toString() || "",
                            }));
                            setComputed(null);
                          }}
                        >
                          <SelectTrigger className="input-field" data-testid="payroll-employee-select">
                            <SelectValue placeholder="Select employee" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((emp) => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.first_name} {emp.last_name} {emp.salary ? `(${fmt(emp.salary)}/mo)` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Month</Label>
                        <Select
                          value={formData.month}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, month: value }))}
                        >
                          <SelectTrigger className="input-field" data-testid="payroll-month-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {months.map((month) => (
                              <SelectItem key={month} value={month}>{month}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Year</Label>
                        <Input
                          type="number"
                          value={formData.year}
                          onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                          className="input-field"
                          data-testid="payroll-year-input"
                        />
                      </div>
                    </div>

                    {/* Pending Advances Alert */}
                    {pendingAdvances.length > 0 && (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg" data-testid="pending-advances-alert">
                        <div className="flex items-center gap-2 text-orange-800 font-medium text-sm mb-2">
                          <IndianRupee className="h-4 w-4" />
                          Pending Advance/Loan Recovery
                        </div>
                        <div className="space-y-1 text-sm">
                          {pendingAdvances.map((adv) => (
                            <div key={adv.id} className="flex justify-between text-orange-700">
                              <span className="capitalize">{adv.type}: {adv.reason || "No reason"}</span>
                              <span className="font-medium">-{fmt(adv.deduction_this_month)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-orange-800 font-semibold border-t border-orange-200 pt-1 mt-1">
                            <span>Total Auto-Deduction</span>
                            <span>-{fmt(pendingAdvances.reduce((sum, a) => sum + a.deduction_this_month, 0))}</span>
                          </div>
                        </div>
                        <p className="text-xs text-orange-600 mt-2">This will be automatically deducted from salary when payroll is created.</p>
                      </div>
                    )}

                    {/* Attendance Summary / LOP Alert */}
                    {formData.employee_id && formData.month && formData.year && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="attendance-summary-alert">
                        <div className="flex items-center gap-2 text-blue-800 font-medium text-sm mb-2">
                          <CalendarDays className="h-4 w-4" />
                          Attendance Summary - {formData.month} {formData.year}
                        </div>
                        {loadingAttendance ? (
                          <div className="flex items-center gap-2 text-blue-600 text-sm">
                            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                            Loading attendance data...
                          </div>
                        ) : attendanceSummary ? (
                          <div className="space-y-1 text-sm">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                              <div className="flex justify-between text-blue-700">
                                <span>Working Days</span>
                                <span className="font-medium">{attendanceSummary.working_days}</span>
                              </div>
                              <div className="flex justify-between text-blue-700">
                                <span>Present Days</span>
                                <span className="font-medium">{attendanceSummary.present_days}</span>
                              </div>
                              {attendanceSummary.half_days > 0 && (
                                <div className="flex justify-between text-blue-700">
                                  <span>Half Days</span>
                                  <span className="font-medium">{attendanceSummary.half_days}</span>
                                </div>
                              )}
                              {attendanceSummary.leave_days > 0 && (
                                <div className="flex justify-between text-green-700">
                                  <span>Approved Leave Days</span>
                                  <span className="font-medium">{attendanceSummary.leave_days}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-blue-700">
                                <span>Absent / LOP Days</span>
                                <span className={`font-medium ${attendanceSummary.lop_days > 0 ? 'text-red-600' : ''}`}>
                                  {attendanceSummary.lop_days}
                                </span>
                              </div>
                            </div>
                            {attendanceSummary.leave_days > 0 && (
                              <p className="text-xs text-green-600 mt-1">✓ {attendanceSummary.leave_days} approved leave day(s) excluded from LOP calculation</p>
                            )}
                            {attendanceSummary.lop_days > 0 && attendanceSummary.lop_amount > 0 && (
                              <div className="flex justify-between text-red-600 font-semibold border-t border-blue-200 pt-1 mt-1">
                                <span>Loss of Pay (LOP) Deduction</span>
                                <span data-testid="lop-deduction-preview">-{fmt(attendanceSummary.lop_amount)}</span>
                              </div>
                            )}
                            {attendanceSummary.lop_days === 0 && (
                              <p className="text-xs text-green-600 mt-1">✓ Full attendance - No LOP deduction</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-blue-600">No attendance records found for this period. LOP will be calculated as 0.</p>
                        )}
                      </div>
                    )}

                    {/* Salary Input */}
                    <div className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-slate-800 text-sm">Earnings</h4>
                        <Button type="button" variant="outline" size="sm" className="rounded-full text-xs" onClick={handleAutoCalculate} disabled={calculating} data-testid="auto-calculate-btn">
                          <Calculator className="h-3 w-3 mr-1" />
                          {calculating ? "Calculating..." : "Auto-Split from CTC"}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Basic Salary / CTC (Monthly)</Label>
                          <Input
                            type="number"
                            value={formData.basic_salary}
                            onChange={(e) => { setFormData(prev => ({ ...prev, basic_salary: e.target.value })); setComputed(null); }}
                            className="input-field"
                            required
                            placeholder="Enter CTC or Basic"
                            data-testid="payroll-basic-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">HRA</Label>
                          <Input
                            type="number"
                            value={formData.hra}
                            onChange={(e) => { setFormData(prev => ({ ...prev, hra: e.target.value })); setComputed(null); }}
                            className="input-field"
                            placeholder="0"
                            data-testid="payroll-hra-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Conveyance</Label>
                          <Input
                            type="number"
                            value={formData.conveyance_allowance}
                            onChange={(e) => { setFormData(prev => ({ ...prev, conveyance_allowance: e.target.value })); setComputed(null); }}
                            className="input-field"
                            placeholder="0"
                            data-testid="payroll-conveyance-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Special Allowance</Label>
                          <Input
                            type="number"
                            value={formData.special_allowance}
                            onChange={(e) => { setFormData(prev => ({ ...prev, special_allowance: e.target.value })); setComputed(null); }}
                            className="input-field"
                            placeholder="0"
                            data-testid="payroll-special-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Statutory Deduction Toggles */}
                    <div className="border rounded-lg p-4 space-y-3">
                      <h4 className="font-medium text-slate-800 text-sm">Statutory Deductions</h4>
                      <div className="flex items-center justify-between py-1">
                        <div>
                          <p className="text-sm font-medium text-slate-700">Provident Fund (PF)</p>
                          <p className="text-xs text-slate-400">12% employee + 12% employer</p>
                        </div>
                        <Switch
                          checked={formData.enable_pf}
                          onCheckedChange={(checked) => { setFormData(prev => ({ ...prev, enable_pf: checked })); setComputed(null); }}
                          data-testid="enable-pf-switch"
                        />
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between py-1">
                        <div>
                          <p className="text-sm font-medium text-slate-700">Employee State Insurance (ESI)</p>
                          <p className="text-xs text-slate-400">0.75% employee + 3.25% employer</p>
                        </div>
                        <Switch
                          checked={formData.enable_esi}
                          onCheckedChange={(checked) => { setFormData(prev => ({ ...prev, enable_esi: checked })); setComputed(null); }}
                          data-testid="enable-esi-switch"
                        />
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between py-1">
                        <div>
                          <p className="text-sm font-medium text-slate-700">Professional Tax (PT)</p>
                          <p className="text-xs text-slate-400">State-based tax (max ₹200/month)</p>
                        </div>
                        <Switch
                          checked={formData.enable_pt}
                          onCheckedChange={(checked) => { setFormData(prev => ({ ...prev, enable_pt: checked })); setComputed(null); }}
                          data-testid="enable-pt-switch"
                        />
                      </div>
                    </div>

                    {/* Custom Deductions */}
                    <div className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-slate-800 text-sm">Custom Deductions</h4>
                        <Button type="button" variant="outline" size="sm" className="rounded-full text-xs" onClick={addCustomDeduction} data-testid="add-custom-deduction-btn">
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      {formData.custom_deductions.map((ded, idx) => (
                        <div key={idx} className="flex gap-2 items-end">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs text-slate-500">Name</Label>
                            <Input
                              value={ded.name}
                              onChange={(e) => updateCustomDeduction(idx, "name", e.target.value)}
                              className="input-field"
                              placeholder="Loan, Advance, etc."
                              data-testid={`custom-ded-name-${idx}`}
                            />
                          </div>
                          <div className="w-32 space-y-1">
                            <Label className="text-xs text-slate-500">Amount</Label>
                            <Input
                              type="number"
                              value={ded.amount || ""}
                              onChange={(e) => updateCustomDeduction(idx, "amount", e.target.value)}
                              className="input-field"
                              placeholder="0"
                              data-testid={`custom-ded-amount-${idx}`}
                            />
                          </div>
                          <Button type="button" variant="ghost" size="sm" className="text-red-500 mb-0.5" onClick={() => removeCustomDeduction(idx)} data-testid={`remove-custom-ded-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {formData.custom_deductions.length === 0 && (
                        <p className="text-xs text-slate-400">No custom deductions added</p>
                      )}
                    </div>

                    {/* Calculate button */}
                    {!computed && (
                      <Button type="button" variant="outline" className="w-full" onClick={handleManualCalculate} disabled={calculating} data-testid="calculate-payroll-btn">
                        <Calculator className="h-4 w-4 mr-2" />
                        {calculating ? "Calculating..." : "Calculate Payroll"}
                      </Button>
                    )}

                    {/* Computed Breakdown */}
                    {computed && (
                      <div className="border rounded-lg p-4 bg-slate-50 space-y-2" data-testid="payroll-breakdown">
                        <h4 className="font-medium text-slate-800 text-sm mb-2">Salary Breakdown</h4>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                          <div className="text-slate-500">Basic Salary</div>
                          <div className="text-right font-medium">{fmt(computed.basic_salary)}</div>
                          <div className="text-slate-500">HRA</div>
                          <div className="text-right font-medium">{fmt(computed.hra)}</div>
                          <div className="text-slate-500">Conveyance</div>
                          <div className="text-right font-medium">{fmt(computed.conveyance_allowance)}</div>
                          <div className="text-slate-500">Special Allowance</div>
                          <div className="text-right font-medium">{fmt(computed.special_allowance)}</div>
                          <div className="text-slate-700 font-semibold border-t pt-1 mt-1">Gross Salary</div>
                          <div className="text-right font-bold text-green-700 border-t pt-1 mt-1">{fmt(computed.gross_salary)}</div>

                          <div className="text-red-600 mt-2">PF (Employee 12%)</div>
                          <div className="text-right text-red-600 mt-2">-{fmt(computed.pf_employee)}</div>
                          <div className="text-red-600">ESI (0.75%)</div>
                          <div className="text-right text-red-600">-{fmt(computed.esi_employee)}</div>
                          {computed.professional_tax > 0 && (
                            <div className="contents">
                              <div className="text-red-600">Professional Tax</div>
                              <div className="text-right text-red-600">-{fmt(computed.professional_tax)}</div>
                            </div>
                          )}
                          <div className="text-red-600">TDS</div>
                          <div className="text-right text-red-600">-{fmt(computed.tds)}</div>
                          {computed.custom_deductions?.map((d, i) => (
                            <div key={`custom-ded-${d.name}-${i}`} className="contents">
                              <div className="text-red-600">{d.name}</div>
                              <div className="text-right text-red-600">-{fmt(d.amount)}</div>
                            </div>
                          ))}
                          {computed.advance_deduction > 0 && (
                            <div className="contents">
                              <div className="text-orange-600 font-medium">Advance/Loan Recovery</div>
                              <div className="text-right text-orange-600 font-medium" data-testid="advance-deduction-preview">-{fmt(computed.advance_deduction)}</div>
                            </div>
                          )}
                          {computed.lop_amount > 0 && (
                            <div className="contents">
                              <div className="text-blue-600 font-medium">Loss of Pay ({computed.lop_days} days)</div>
                              <div className="text-right text-blue-600 font-medium" data-testid="lop-deduction-computed">-{fmt(computed.lop_amount)}</div>
                            </div>
                          )}
                          <div className="text-slate-700 font-semibold border-t pt-1 mt-1">Total Deductions</div>
                          <div className="text-right font-bold text-red-600 border-t pt-1 mt-1">-{fmt(computed.total_deductions)}</div>

                          <div className="text-slate-800 font-bold text-base border-t-2 pt-2 mt-2">Net Salary</div>
                          <div className="text-right font-bold text-base text-primary border-t-2 pt-2 mt-2">{fmt(computed.net_salary)}</div>
                        </div>
                        <div className="text-xs text-slate-400 mt-2">Employer PF: {fmt(computed.pf_employer)} | Employer ESI: {fmt(computed.esi_employer)}</div>
                      </div>
                    )}

                    <Button type="submit" className="w-full btn-primary" disabled={!computed} data-testid="save-payroll-btn">
                      Create Payroll
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Stats - using PayrollStats component */}
      <PayrollStats 
        stats={{
          totalRecords: stats.total,
          totalPayout: stats.totalProcessed,
          pending: stats.pending,
          processed: stats.processed,
        }} 
        currency={currentBusiness?.currency} 
      />

      {/* Payroll Table - using PayrollTable component */}
      <PayrollTable
        payrolls={payrolls}
        loading={loading}
        getEmployeeName={getEmployeeName}
        onViewDetail={viewPayrollDetail}
        onDownloadPayslip={handleDownloadPayslip}
        onProcess={canManagePayroll ? handleProcess : undefined}
        currency={currentBusiness?.currency}
      />

      {/* Payslip Detail Dialog - using PayslipDialog component */}
      <PayslipDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        payroll={selectedPayroll}
        getEmployeeName={getEmployeeName}
        onDownload={handleDownloadPayslip}
        onProcess={handleProcess}
        currency={currentBusiness?.currency}
      />
    </div>
  );
};

export default Payroll;
